import React from "react";
import {
    Alert,
    Autocomplete,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    FormControl,
    Grid,
    Grow,
    IconButton,
    MenuItem,
    Paper,
    Select,
    Snackbar,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    TableSortLabel,
    TextField
} from "@mui/material";
import {visuallyHidden} from '@mui/utils';
import InputLabel from '@mui/material/InputLabel';
import PropTypes from 'prop-types';
import PersonIcon from '@mui/icons-material/Person';
import AddIcon from '@mui/icons-material/Add';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import AttendanceData from "../../backend/dataservices/AttendanceData";
import User from "../../backend/User";
import {useLocation, useNavigate} from "react-router-dom";
import EmployeeData from "../../backend/dataservices/EmployeeData";
import TableUtils from "../../utils/TableUtils";
import TableDateChooser from "../TableDateChooser";
import CreateNewAttendanceDialogComponent from "../CreateNewAttendanceDialogComponent";
import config from '../../utils/Config'
import moment from "moment";

function createData(courseName, status, date, duration, notes, last_change, courseID, type, attendanceID, employeeName, course, employeeID, typ) {
    return {courseName, status, date, duration, notes, last_change, courseID, type, attendanceID, employeeName, course, employeeID, typ};
}

function createSuggestionsData(_name, _employee) {
    return {name: _name, employee: _employee};
}

function descendingComparator(a, b, orderBy) {
    if (orderBy === 'date') {
        let dateA;
        let dateB;
        if(a[orderBy].length >= 11) {
            dateA = moment(a[orderBy].substring(0, 10), "DD.MM.YYYY").toDate();
        } else {
            dateA = moment(a[orderBy], "YYYY").toDate();
        }

        if(b[orderBy].length >= 11){
            dateB = moment(b[orderBy].substring(0, 10), "DD.MM.YYYY").toDate();
        } else {
            dateB = moment(b[orderBy], "YYYY").toDate();
        }

        if (dateB < dateA) {
            return -1;
        }
        if (dateB > dateA) {
            return 1;
        }
        return 0;
    }

    if (b[orderBy] < a[orderBy]) {
        return -1;
    }
    if (b[orderBy] > a[orderBy]) {
        return 1;
    }
    return 0;
}

function getComparator(order, orderBy) {
    return order === 'desc'
        ? (a, b) => descendingComparator(a, b, orderBy)
        : (a, b) => -descendingComparator(a, b, orderBy);
}

// This method is created for cross-browser compatibility, if you don't
// need to support IE11, you can use Array.prototype.sort() directly
function stableSort(array, comparator) {
    const stabilizedThis = array.map((el, index) => [el, index]);
    stabilizedThis.sort((a, b) => {
        const order = comparator(a[0], b[0]);
        if (order !== 0) {
            return order;
        }
        return a[1] - b[1];
    });
    return stabilizedThis.map((el) => el[0]);
}

const headCells = [
    {
        id: 'courseName',
        numeric: false,
        disablePadding: false,
        label: 'Kursname',
    },
    {
        id: 'status',
        numeric: false,
        disablePadding: false,
        label: 'Status',
    },
    {
        id: 'date',
        numeric: false,
        disablePadding: false,
        label: 'Termin',
    },
    {
        id: 'duration',
        numeric: false,
        disablePadding: false,
        label: 'Dauer',
    },
    {
        id: 'notes',
        numeric: false,
        disablePadding: false,
        label: 'Bemerkungen',
    },
    {
        id: 'typ',
        numeric: false,
        disablePadding: false,
        label: 'Typ',
    },
    {
        id: 'last_change',
        numeric: false,
        disablePadding: false,
        label: 'Letzte Bearbeitung',
    },
    {
        id: 'delete',
        numeric: false,
        disablePadding: false,
        label: '',
    },
]

function EnhancedTableHead(props) {
    const {order, orderBy, onRequestSort} =
        props;
    const createSortHandler = (property) => (event) => {
        onRequestSort(event, property);
    };

    return (
        <TableHead>
            <TableRow>
                {headCells.map((headCell) => (
                    <TableCell
                        key={headCell.id}
                        align={headCell.numeric ? 'right' : 'left'}
                        padding={headCell.disablePadding ? 'none' : 'normal'}
                        sortDirection={orderBy === headCell.id ? order : false}
                    >
                        <TableSortLabel
                            active={orderBy === headCell.id}
                            direction={orderBy === headCell.id ? order : 'asc'}
                            onClick={createSortHandler(headCell.id)}
                        >
                            {headCell.label}
                            {orderBy === headCell.id ? (
                                <Box component="span" sx={visuallyHidden}>
                                    {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                                </Box>
                            ) : null}
                        </TableSortLabel>
                    </TableCell>
                ))}
            </TableRow>
        </TableHead>
    );
}

EnhancedTableHead.propTypes = {
    onRequestSort: PropTypes.func.isRequired,
    order: PropTypes.oneOf(['asc', 'desc']).isRequired,
    orderBy: PropTypes.string.isRequired,
    rowCount: PropTypes.number.isRequired,
};

export default function EmployeesTable(props) {


    const [suggestions, setSuggestions] = React.useState([]);
    const [openSuggestion, setOpenSuggestion] = React.useState(false);
    const [suggestionValue, setSuggestionValue] = React.useState(null);
    const [inputValue, setInputValue] = React.useState("");
    const [order, setOrder] = React.useState('desc');
    const [orderBy, setOrderBy] = React.useState('date');
    const [page, setPage] = React.useState(0);
    const [rowsPerPage, setRowsPerPage] = React.useState(10);
    const [rows, setRows] = React.useState([]);
    const [employeeData, setEmployeeData] = React.useState(null);
    const [openDialog, setOpenDialog] = React.useState(false);
    const [loading, setLoading] = React.useState(false);
    const [showAttendanceDeleteDialog, setShowAttendanceDeleteDialog] = React.useState(false)
    const [attendanceDeleteDialogData, setAttendanceDeleteDialogData] = React.useState({})
    const [snackbarOpen, setSnackbarOpen] = React.useState(false);
    const [snackbarText, setSnackbarText] = React.useState("");
    const [snackbarSeverity, setSnackbarSeverity] = React.useState("success");
    const [openDateChooserDialog, setOpenDateChooserDialog] = React.useState(false);
    const [dateChooserDialogData, setDateChooserDialogData] = React.useState({});
    const [stateSelectorValue, setStateSelectorValue] = React.useState(new Map());
    const [textfieldValue, setTextfieldValue] = React.useState(new Map());
    const [typeSelectorValue, setTypeSelectorValue] = React.useState(new Map());
    const navigate = useNavigate();
    const location = useLocation();


    React.useEffect(() => {
        (async () => {
            if(location.state != null && location.state.name != null && location.state.name.length !== 0 ) {
                sessionStorage.setItem('employee', location.state.id);
                EmployeeData.getEmployeeBaseData(location.state.id).then((employeeData) => {
                    let employee = employeeData[0];
                    setSuggestionValue(createSuggestionsData(location.state.name, employee))
                    fetchAttendanceData(employee).then((atts) => {
                        createEmployeeData(employee, atts);
                    });
                });
            } else {
                if(sessionStorage.getItem('employee')) {
                    EmployeeData.getEmployeeBaseData(sessionStorage.getItem('employee')).then((employeeData) => {
                        let employee = employeeData[0];
                        setSuggestionValue(createSuggestionsData(employee.M_Name + ", " + employee.M_Vorname, employee))
                        fetchAttendanceData(employee).then((atts) => {
                            createEmployeeData(employee, atts);
                        });
                    });
                }
            }
        })();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps


    function searchEmployee(name) {
        setLoading(true);
        EmployeeData.searchEmployeeByName(name).then((v) => {
            generateRows(v);
        });
    }


    async function loadDataFromDatabase(rawEmployeeData) {
        let tempRows = [];
        for (const employee of rawEmployeeData) {
            tempRows.push(createSuggestionsData(employee.M_Name + ", " + employee.M_Vorname, employee));
        }
        return tempRows;
    }

    function generateRows(rawEmployeeData) {
        loadDataFromDatabase(rawEmployeeData).then(data => {
            setSuggestions(data);
            setLoading(false);
        });
    }

    const handleRequestSort = (event, property) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    function createEmployeeData(employee, atts) {
        setEmployeeData({
            name: employee.M_Name + ', ' + employee.M_Vorname,
            gid: employee['M_Pers-Nr'],
            cost_center: employee.M_Kostenstelle,
            plannedHours: calculateHoursForThisYear(atts)
        })
    }

    function handleInputValue(value) {
        if (value != null) {
            let employee = value.employee;
            if (employee != null) {
                fetchAttendanceData(employee)
                    .then(atts => {
                        createEmployeeData(employee, atts);
                    })
            }
        } else {
            setEmployeeData(null)
            setRows([]);
        }
    }

    function fetchAttendanceData(employee) {
        return AttendanceData.fetchAllAttendancesForEmployee(employee['M_Pers-Nr'])
            .then(atts => {
                let tempRows = [];
                if (!Array.isArray(atts)) { setRows([]); return; }
                atts.forEach(function (value, key) {
                    let attendanceID = value._type === 'plannedAttendance'
                        ? value.KA_ID
                        : value.KV_ID;
                    tempRows.push(createData(
                       value.KS_Kursname,
                       value._type === 'plannedAttendance' ? capitalizeFirstLetter(value.KA_Status) : capitalizeFirstLetter(value.KV_Statustext),
                       dateFormatter(value),
                       TableUtils.durationFormatter(value),
                       commentValueGetter(value),
                       value._type === 'attendance' ? value.KV_Bearbeiter : value.KA_Bearbeiter,
                       value.KS_ID,
                       value._type,
                       attendanceID,
                       employee.M_Name + ', ' + employee.M_Vorname,
                       value,
                       employee['M_Pers-Nr'],
                       value._type === 'plannedAttendance' ? value.KA_Typ : value.KV_Typ
                   ));
                    setTextfieldValue(new Map(textfieldValue.set(attendanceID, commentValueGetter(value))));
                    setTypeSelectorValue(new Map(typeSelectorValue.set(attendanceID, value._type === 'plannedAttendance' ? value.KA_Typ : value.KV_Typ)))
                });
                setRows(tempRows);
                return atts
            })
    }

    function capitalizeFirstLetter(string) {
        return string.charAt(0).toUpperCase() + string.slice(1);
    }

    function commentValueGetter(_value) {
        let value =
            _value._type === 'attendance'
                ? _value.KV_Bemerkungen
                : _value.KA_Bemerkungen
        if (value == null) {
            return ""
        } else {
            return value
        }
    }

    function calculateHoursForThisYear(atts) {
        const year = new Date().getFullYear()

        return atts.filter(att => {
            let date;
            if(att._type === 'attendance')
                date = att.KD_Anfangstag
            else
                date = att.KA_Datum

            date = new Date(date)

            return date.getFullYear() === year
        }).reduce((sum, att) => {
            return sum + att.KS_Dauer
        }, 0)
    }

    function dateFormatter(_value) {
        let _type = _value._type
        let value;

        if (_type === 'attendance') {
            value = [_value.KD_Anfangstag, _value.KD_Letzter_Tag];
        } else {
            value = _value.KA_Datum;
        }

        let dateOptions = {year: 'numeric', month: '2-digit', day: '2-digit'}

        if (_type === 'plannedAttendance') {
            dateOptions = {year: 'numeric'}
            const date = new Date(value)
            return `${date.toLocaleDateString('de-DE', dateOptions)}`
        }

        if (value[0] == null || value[1] == null) {
            return '-'
        }

        const begin = new Date(value[0])
        const end = new Date(value[1])
        return `${begin.toLocaleDateString('de-DE', dateOptions)} - ${end.toLocaleDateString('de-DE', dateOptions)}`
    }

    function openSCD() {
        window.open(config.scd_url + employeeData.gid, '_blank', 'noopener,noreferrer')
    }

    function handleCloseDialog() {
        setOpenDialog(false);
    }

    function closeSnackbar() {
        setSnackbarOpen(false);
    }

    const toCourses = (courseName, courseID) => {
        navigate("/courses", {state: {id: courseID, name: courseName}});
    }

    function deleteCourse() {
        AttendanceData.deletePlannedAttendance(attendanceDeleteDialogData.attendanceID).then(value => {
            handleInputValue(suggestionValue);
            setSnackbarSeverity("success");
            setSnackbarText('Kurszuweisung erfolgreich gelöscht!')
            setSnackbarOpen(true);
        }, (error) => {
            setSnackbarSeverity("error");
            setSnackbarText('Fehler beim Löschen der Kurszuweisung!');
            setSnackbarOpen(true);
        }).then(value => {
            setShowAttendanceDeleteDialog(false);
            setAttendanceDeleteDialogData({});
        })
    }

    function handleAttendanceDialogClose() {
        setShowAttendanceDeleteDialog(false);
        setAttendanceDeleteDialogData({});
    }

    function handleNoteChange(attendanceID, type) {
        AttendanceData.saveAttendanceComment(attendanceID, textfieldValue.has(attendanceID) ? textfieldValue.get(attendanceID) : '', type).then(value => {
            setSnackbarSeverity("success");
            setSnackbarText('Bemerkung gespeichert');
            setSnackbarOpen(true);
            handleInputValue(suggestionValue)
        }, (error) => {
            setSnackbarSeverity("error");
            setSnackbarText('Fehler beim Schreiben der Bemerkung');
            setSnackbarOpen(true);
        });
    }

    function handleNoteChangeEnter(event, attendanceID, type) {
        if (event.key === 'Enter') {
            event.target.blur();
            handleNoteChange(attendanceID, type);
        }
    }

    function closeDateChooserDialog() {
        setOpenDateChooserDialog(false);
        handleInputValue(suggestionValue)
        setSnackbarSeverity("success");
        setSnackbarText("Status erfolgreich geändert!")
        setSnackbarOpen(true)
    }


    function abortChanges(attendanceID) {
        setOpenDateChooserDialog(false);
        setSnackbarSeverity("error");
        setSnackbarText("Ein unerwarteter Fehler ist aufgetreten")
        setSnackbarOpen(true);
        let newMap = stateSelectorValue;
        newMap.set(attendanceID, dateChooserDialogData.oldValue);
        setStateSelectorValue(newMap);
    }

    async function handleTypChange(attendanceID, type, newValue) {
        if(type === 'plannedAttendance'){
            AttendanceData.updatePlannedTypeAttendance(attendanceID, newValue).then(value => {
                setTypeSelectorValue(new Map(typeSelectorValue.set(attendanceID, newValue)));
                setSnackbarSeverity("success");
                setSnackbarText("Typ erfolgreich geändert!");
                setSnackbarOpen(true);
            }, (error) => {
                setSnackbarSeverity("error");
                setSnackbarText("Ein unerwarteter Fehler ist aufgetreten");
                setSnackbarOpen(true);
            }).then(() => {
                handleInputValue(suggestionValue);
            })
        } else {
            AttendanceData.changeAttendanceType(attendanceID, newValue).then(value => {
                setTypeSelectorValue(new Map(typeSelectorValue.set(attendanceID, newValue)));
                setSnackbarSeverity("success");
                setSnackbarText("Typ erfolgreich geändert!");
                setSnackbarOpen(true);
            }, (error) => {
                setSnackbarSeverity("error");
                setSnackbarText("Ein unerwarteter Fehler ist aufgetreten");
                setSnackbarOpen(true);
            }).then(() => {
                handleInputValue(suggestionValue);
            });
        }
    }

    return (
        <Box sx={{width: '98%'}} style={{
            position: 'absolute', left: '50%',
            transform: 'translate(-50%)'
        }}>
            <Paper sx={{width: '100%', mb: 2}}>
                <div style={{display: "flex", flexDirection: "row"}}>
                    <Autocomplete options={suggestions}
                                  sx={{marginLeft: '1%', marginTop: '10px', width: '30%'}}
                                  open={openSuggestion}
                                  onOpen={() => {
                                      setOpenSuggestion(true)
                                  }}
                                  onClose={() => {
                                      setOpenSuggestion(false)
                                  }}

                                  inputValue={inputValue}
                                  onInputChange={(event, newInputValue) => {
                                      setInputValue(newInputValue)
                                      if(newInputValue.length >= 2) {
                                          searchEmployee(newInputValue);
                                      }
                                  }}

                                  value={suggestionValue}
                                  onChange={(event, newInputValue) => {
                                      setSuggestionValue(newInputValue);
                                      sessionStorage.setItem('employee', newInputValue.employee['M_Pers-Nr']);
                                      handleInputValue(newInputValue);
                                  }}
                                  renderOption={(props, option) => {
                                      return (
                                          <li {...props} key={option.employee['M_Pers-Nr']}>
                                              {option.name}
                                          </li>
                                      );
                                  }}
                                  isOptionEqualToValue={(option, value) => option.name.includes(value.name)}
                                  getOptionLabel={(option) => option.name}
                                  renderInput={(params => (
                                      <TextField
                                          {...params}
                                          variant={"outlined"}
                                          label={"Suche..."}
                                          sx={{width: '100%', marginTop: '30px'}}
                                          InputProps={{
                                              ...params.InputProps,
                                              endAdornment: (
                                                  <React.Fragment>
                                                      {loading ? <CircularProgress color={"primary"}/> : null}
                                                      {params.InputProps.endAdornment}
                                                  </React.Fragment>
                                              ),
                                          }}
                                      />
                                  ))}/>
                    {employeeData !== null ?
                        <Box margin={'30px 0% 30px 10%'} style={{display: "flex", flexDirection: "row"}}>
                            <Grid container spacing={2}>
                                <Grid item xs={3}>
                                    <div>
                                        <b>Name: </b> {employeeData.name}
                                    </div>
                                </Grid>
                                <Grid item xs={3}>
                                    <div>
                                        <b>GID: </b> {employeeData.gid}
                                    </div>
                                </Grid>
                                <Grid item xs={3}>
                                    <div>
                                        <b>Kostenstelle: </b> {employeeData.cost_center}
                                    </div>
                                </Grid>
                                <Grid item xs={3}>
                                    <Button color={"primary"} variant={"outlined"} startIcon={<PersonIcon/>}
                                            onClick={openSCD}>
                                        SCD
                                    </Button>
                                </Grid>
                                <Grid item xs={12}>
                                    <div style={{textAlign: 'center'}}>
                                        <b>Geplante Kursstunden dieses Jahr: </b> {employeeData.plannedHours}
                                    </div>
                                </Grid>
                            </Grid>
                            <Box marginRight={'20px'}>
                                <Button variant={"contained"} endIcon={<AddIcon/>} onClick={() => {
                                    setOpenDialog(true);
                                }}>
                                    Neue Kursanmeldung
                                </Button>
                            </Box>
                        </Box> : null}
                </div>

                <CreateNewAttendanceDialogComponent
                    openDialog={openDialog}
                    handleClose={handleCloseDialog}
                    handleSuccess={() => {
                        setOpenDialog(false);
                        setSnackbarText("Neue Kursanmeldung für " + employeeData.name + " erfolgreich!");
                        setSnackbarSeverity("success");
                        setSnackbarOpen(true);
                        handleInputValue(suggestionValue);
                    }}
                    handleError={() => {
                        setOpenDialog(false);
                        setSnackbarText("Bei der Kursanmeldung ist ein unerwarteter Fehler aufetreten")
                        setSnackbarSeverity("error");
                        setSnackbarOpen(true);
                    }}
                    searchCourses={true} employeeData={employeeData}/>

                <TableContainer>
                    <Table
                        sx={{minWidth: 750}}
                        width={'80%'}
                        aria-labelledby="tableTitle"
                        size={'medium'}
                    >
                        <EnhancedTableHead
                            order={order}
                            orderBy={orderBy}
                            onRequestSort={handleRequestSort}
                            rowCount={rows.length}
                        />
                        <TableBody>
                            {/* if you don't need to support IE11, you can replace the `stableSort` call with:
                 suggestions.slice().sort(getComparator(order, orderBy)) */}
                            {stableSort(rows, getComparator(order, orderBy))
                                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                .map((row, index) => {
                                    const labelId = `enhanced-table-checkbox-${index}`;
                                    return (
                                        <TableRow
                                            hover
                                            role="checkbox"
                                            tabIndex={-1}
                                            key={index}
                                        >
                                            <TableCell
                                                component="th"
                                                id={labelId}
                                                scope="row"
                                                padding="none"
                                                style={{cursor: "pointer"}}
                                                onClick={() => toCourses(row.courseName, row.courseID)}
                                            >
                                                {row.courseName}
                                            </TableCell>
                                            <TableCell align="left">
                                                {User.getRole() === 'Verwalter' ?
                                                    <FormControl fullWidth size={"small"}>
                                                        <InputLabel>Status</InputLabel>
                                                        <Select
                                                            fullWidth
                                                            value={stateSelectorValue.get(row.attendanceID) == null ? row.status : stateSelectorValue.get(row.attendanceID)}
                                                            label={"Status"}
                                                            onChange={(event) => {
                                                                setDateChooserDialogData({
                                                                    oldValue: row.status,
                                                                    newValue: event.target.value,
                                                                    employeeName: row.employeeName,
                                                                    course: row.course,
                                                                    employeeID: row.employeeID,
                                                                    attendanceID: row.attendanceID,
                                                                    type: typeSelectorValue.get(row.attendanceID)
                                                                });
                                                                let newMap = stateSelectorValue;
                                                                newMap.set(row.attendanceID, event.target.value);
                                                                setStateSelectorValue(newMap);
                                                                let openDateChooser = TableUtils.handleStatusChangerChange(event, row);
                                                                if(openDateChooser)
                                                                    setOpenDateChooserDialog(openDateChooser);
                                                                else {
                                                                    TableUtils.changeState(event, row, event.target.value).then((value) => {
                                                                        setSnackbarSeverity("success");
                                                                        setSnackbarText("Status erfolgreich geändert!");
                                                                        setSnackbarOpen(true);
                                                                    }, (error) => {
                                                                        setSnackbarSeverity("error");
                                                                        setSnackbarText("Ein unerwarteter Fehler ist aufgetreten");
                                                                        setSnackbarOpen(false);
                                                                    });
                                                                }
                                                            }}
                                                            defaultValue={row.status}
                                                        >
                                                            {TableUtils.states.map(function(value) {
                                                                return <MenuItem key={value.name} value={value.name}>{value.name}</MenuItem>
                                                            })}
                                                        </Select>
                                                    </FormControl>
                                                    :
                                                    row.status}
                                            </TableCell>
                                            <TableCell align="left">{row.date}</TableCell>
                                            <TableCell align="left">{row.duration}</TableCell>
                                            <TableCell align="left">
                                                <TextField
                                                    variant={"standard"}
                                                    value={textfieldValue.get(row.attendanceID)}
                                                    onBlur={(e) => {
                                                        handleNoteChange(row.attendanceID, row.type);
                                                    }}
                                                    onChange={(e) => {
                                                        setTextfieldValue(new Map(textfieldValue.set(row.attendanceID, e.target.value)))
                                                    }}
                                                    fullWidth
                                                    InputProps={{disableUnderline: true}}
                                                    onKeyDown={(e) => {
                                                        if (e.key === 'Enter') {
                                                            handleNoteChangeEnter(e, row.attendanceID, row.type);
                                                        }
                                                    }}/>
                                            </TableCell>
                                            <TableCell align="left" style={{width: '13%'}}>
                                                {User.getRole() === 'Verwalter' ?
                                                <FormControl fullWidth size={"small"}>
                                                    <InputLabel>Typ</InputLabel>
                                                    <Select
                                                        required
                                                        fullWidth
                                                        displayEmpty
                                                        label="Typ"
                                                        value={typeSelectorValue.get(row.attendanceID) == null ? '' : typeSelectorValue.get(row.attendanceID)}
                                                        onChange={(event) => {
                                                            handleTypChange(row.attendanceID, row.type, event.target.value);
                                                        }}
                                                        MenuProps={{style: {maxHeight: 200}}}>
                                                        {TableUtils.getTypes().map(function (typ) {
                                                            return <MenuItem key={typ} value={typ}>{typ}</MenuItem>
                                                        })
                                                        }
                                                    </Select>
                                                </FormControl>
                                                :
                                                    typeSelectorValue.get(row.attendanceID) == null ? '' : typeSelectorValue.get(row.attendanceID)}
                                            </TableCell>
                                            <TableCell align="left">{row.last_change}</TableCell>
                                            <TableCell align="left">
                                                {(row.type === "plannedAttendance" && row.status === 'Geplant') ? (
                                                    <IconButton size={"large"} color={"error"} onClick={() => {
                                                        setAttendanceDeleteDialogData({name: suggestionValue.name, courseName: row.courseName, courseID: row.courseID, attendanceID: row.attendanceID});
                                                        setShowAttendanceDeleteDialog(true);
                                                    }}>
                                                        <DeleteForeverIcon/>
                                                    </IconButton>
                                                    )
                                                    :
                                                    null
                                                }
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    rowsPerPageOptions={[5, 10, 20]}
                    component="div"
                    count={rows.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                />
            </Paper>

            <Dialog open={showAttendanceDeleteDialog} onClose={handleAttendanceDialogClose}>
                <DialogTitle>
                    Geplanten Kurs löschen
                </DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Möchten Sie den
                        Kurs {attendanceDeleteDialogData.courseName} für {attendanceDeleteDialogData.name} wirklich
                        löschen?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleAttendanceDialogClose} variant={"text"}
                            style={{color: "gray"}}>Abbrechen</Button>
                    <Button color={"error"} variant={"text"} onClick={deleteCourse}>Löschen</Button>
                </DialogActions>
            </Dialog>

            {openDateChooserDialog ?
                <TableDateChooser
                    openDialog={openDateChooserDialog}
                    onClose={closeDateChooserDialog}
                    attendanceID={dateChooserDialogData.attendanceID}
                    employeeName={dateChooserDialogData.employeeName}
                    employeeID={dateChooserDialogData.employeeID}
                    course={dateChooserDialogData.course}
                    newValue={dateChooserDialogData.newValue}
                    type={dateChooserDialogData.type}

                    abortChanges={() => abortChanges(dateChooserDialogData.attendanceID)}/>
                :
                null
            }

            <Snackbar open={snackbarOpen} autoHideDuration={5000} onClose={closeSnackbar}
                      anchorOrigin={{vertical: "top", horizontal: "right"}} TransitionComponent={Grow}>
                <Alert onClose={closeSnackbar} severity={snackbarSeverity} sx={{width: '100%'}}>
                    {snackbarText}
                </Alert>
            </Snackbar>
        </Box>
    );
}

